// Cuando es seguro recargar sin preguntar.
//
// Recargar solo es aceptable si no le pisamos trabajo a nadie. Dos señales:
//
//   - Ociosidad: hace rato que no hay input del usuario.
//   - Trabajo a medias: hay campos con contenido, o alguna pantalla se
//     registro explicitamente como ocupada (un upload, un wizard).
//
// La heuristica de campos es deliberadamente conservadora — prefiere
// posponer una recarga automatica antes que borrarle un formulario a alguien.
// La convergencia no depende de ella: si el usuario nunca queda ocioso, el
// guard igual escala a un banner que no se puede cerrar.

let lastActivityAt = Date.now();
let listening = false;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

function touch(): void {
  lastActivityAt = Date.now();
}

export function startActivityTracking(): () => void {
  if (listening) return () => undefined;
  listening = true;
  for (const ev of ACTIVITY_EVENTS) {
    window.addEventListener(ev, touch, { passive: true, capture: true });
  }
  return () => {
    for (const ev of ACTIVITY_EVENTS) {
      window.removeEventListener(ev, touch, { capture: true });
    }
    listening = false;
  };
}

export function idleForMs(): number {
  return Date.now() - lastActivityAt;
}

// --- Registro explicito de "estoy ocupado" ----------------------------------

let busyCount = 0;

/**
 * Marca trabajo en curso que una recarga arruinaria. Devuelve la funcion para
 * liberarlo. Pensado para uploads, wizards de varios pasos, etc.
 */
export function markBusy(): () => void {
  busyCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    busyCount = Math.max(0, busyCount - 1);
  };
}

const IGNORED_INPUT_TYPES = new Set([
  'hidden',
  'search',
  'checkbox',
  'radio',
  'submit',
  'button',
  'reset',
  'range',
  'color',
]);

/** Heuristica: campos de texto con contenido que el usuario no guardo. */
function hasFilledFields(): boolean {
  const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'input, textarea',
  );
  for (const el of inputs) {
    if (el.disabled || el.readOnly) continue;
    if (el instanceof HTMLInputElement && IGNORED_INPUT_TYPES.has(el.type)) continue;
    // Campos con valor puesto por la app (defaultValue) no cuentan: no es
    // trabajo del usuario.
    if (el.value.length > 0 && el.value !== el.defaultValue) return true;
  }
  // Editores ricos / celdas editables.
  if (document.querySelector('[contenteditable="true"]')) return true;
  return false;
}

/** Un dialogo abierto es una decision pendiente — no recargamos encima. */
function hasOpenDialog(): boolean {
  // `<dialog>` vive siempre en el DOM (ver ConfirmDialog del admin): lo que
  // indica que esta abierto es el atributo `open`, no su presencia.
  if (document.querySelector('dialog[open]') !== null) return true;

  // Los modales hechos a mano tampoco se pueden dar por abiertos solo porque
  // existen — muchos quedan montados y ocultos. Exigimos que se vean; si no,
  // un modal escondido bloquearia la recarga automatica para siempre.
  for (const el of document.querySelectorAll('[role="dialog"], [aria-modal="true"]')) {
    if (isVisible(el)) return true;
  }
  return false;
}

function isVisible(el: Element): boolean {
  if ('checkVisibility' in el && typeof el.checkVisibility === 'function') {
    return el.checkVisibility();
  }
  return el instanceof HTMLElement && el.offsetParent !== null;
}

export function hasWorkInProgress(): boolean {
  return busyCount > 0 || hasOpenDialog() || hasFilledFields();
}

/**
 * La URL lleva estado que una recarga podria consumir o perder?
 *
 * En el login-portal la respuesta es que si: un `code` de OAuth, un token de
 * verificacion o el `pt` de retorno de pago son de un solo uso. Recargar esa
 * URL no es transparente — puede dejar al usuario en un error en medio del
 * flujo. Preferimos no actualizar sola una pantalla asi y quedarnos con el
 * banner, que es honesto, antes que romperle el alta a alguien.
 */
function urlCarriesFlowState(): boolean {
  return window.location.search.length > 1 || window.location.hash.length > 1;
}

/** Se puede recargar sin avisar? */
export function isSafeToReload(
  idleThresholdMs: number,
  urlStateIsFragile: boolean,
): boolean {
  if (hasWorkInProgress()) return false;
  if (urlStateIsFragile && urlCarriesFlowState()) return false;
  // Pestaña oculta = el mejor momento posible: el usuario ni se entera, y
  // cuando vuelve ya esta actualizado. No exigimos ociosidad ahi.
  if (document.visibilityState === 'hidden') return true;
  return idleForMs() >= idleThresholdMs;
}
